export interface ScannedProduct {
  title: string
  editionLabel: string
  priceNumber: number
  priceText: string
  thumbnailUrl?: string
}

// ---- Fallback catalog (used when nothing is detected on the page) ----
export const FALLBACK_PRODUCTS: { product: ScannedProduct; weight: number }[] =
  [
    {
      weight: 0.7,
      product: {
        title: 'Prompt Labs',
        editionLabel: 'Acceso de por vida',
        priceNumber: 13.99,
        priceText: '$13.99',
      },
    },
    {
      weight: 0.3,
      product: {
        title: 'Prompt Labs + Bonos',
        editionLabel: 'Ecosistema Nano-Creative',
        priceNumber: 13.99,
        priceText: '$13.99',
      },
    },
  ]

// Titles coming from the preview/editor chrome that must never be used.
const TITLE_BLOCKLIST = [
  'internal preview',
  'preview',
  'magic patterns',
  'untitled',
  'document',
]

function isValidTitle(title: string | undefined): title is string {
  if (!title) return false
  const t = title.trim().toLowerCase()
  if (t.length < 3) return false
  return !TITLE_BLOCKLIST.some((bad) => t === bad || t.includes(bad))
}

const CTA_KEYWORDS = [
  'add to cart',
  'buy',
  'checkout',
  'order',
  'comprar',
  'añadir al carrito',
  'agregar al carrito',
  'acceso instant',
  'quiero acceso',
]

// Matches $27, $27.00, 27 USD, USD 27.00, etc. Captures the numeric portion.
const PRICE_REGEX =
  /(?:USD\s*)?\$\s?(\d{1,4}(?:[.,]\d{2})?)|(\d{1,4}(?:[.,]\d{2})?)\s*USD/i

function parsePrice(text: string): { num: number; text: string } | null {
  if (!text) return null
  const m = text.match(PRICE_REGEX)
  if (!m) return null
  const raw = m[1] ?? m[2]
  if (!raw) return null
  const num = parseFloat(raw.replace(',', '.'))
  if (!Number.isFinite(num) || num <= 0) return null
  return { num, text: m[0].trim() }
}

function pickRandomFallback(): ScannedProduct {
  const r = Math.random()
  let acc = 0
  for (const entry of FALLBACK_PRODUCTS) {
    acc += entry.weight
    if (r <= acc) return entry.product
  }
  return FALLBACK_PRODUCTS[0].product
}

// ---- Structured data (JSON-LD) extraction ----
function fromJsonLd(): ScannedProduct[] {
  if (typeof document === 'undefined') return []
  const products: ScannedProduct[] = []
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[type="application/ld+json"]',
  )
  scripts.forEach((s) => {
    try {
      const data = JSON.parse(s.textContent || '')
      const nodes = Array.isArray(data) ? data : [data]
      nodes.forEach((node: any) => {
        const graph = node['@graph'] ? node['@graph'] : [node]
        graph.forEach((g: any) => {
          if (!g || (g['@type'] !== 'Product' && g['@type'] !== 'product'))
            return
          const offer = Array.isArray(g.offers) ? g.offers[0] : g.offers
          const priceNum = offer ? parseFloat(offer.price) : NaN
          if (!g.name || !Number.isFinite(priceNum)) return
          const currency = offer?.priceCurrency === 'USD' ? '$' : ''
          products.push({
            title: String(g.name),
            editionLabel: 'Edición',
            priceNumber: priceNum,
            priceText: `${currency}${priceNum}`,
            thumbnailUrl: Array.isArray(g.image) ? g.image[0] : g.image,
          })
        })
      })
    } catch {
      /* ignore malformed JSON-LD */
    }
  })
  return products
}

function findNearbyImage(el: Element): string | undefined {
  // Look within the element, then walk up to 3 ancestors for an <img>.
  let node: Element | null = el
  for (let i = 0; i < 4 && node; i++) {
    const img = node.querySelector?.('img')
    if (img && (img as HTMLImageElement).src) {
      return (img as HTMLImageElement).src
    }
    node = node.parentElement
  }
  return undefined
}

function findTitleNear(el: Element): string | undefined {
  let node: Element | null = el
  for (let i = 0; i < 4 && node; i++) {
    const heading = node.querySelector?.('h1, h2, h3, h4')
    const text = heading?.textContent?.trim()
    if (text && text.length >= 3 && text.length <= 80) return text
    node = node.parentElement
  }
  return undefined
}

// ---- DOM heuristic extraction ----
function fromDomHeuristics(): ScannedProduct[] {
  if (typeof document === 'undefined') return []
  const products: ScannedProduct[] = []
  const seen = new Set<string>()

  // Find CTA-like elements, then resolve price/title/image from their context.
  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>('a, button, [role="button"]'),
  ).filter((el) => {
    const t = (el.textContent || '').toLowerCase()
    return CTA_KEYWORDS.some((k) => t.includes(k))
  })

  candidates.forEach((cta) => {
    // Walk up to find a container that also holds a price.
    let container: Element | null = cta
    let priceInfo: { num: number; text: string } | null = null
    for (let i = 0; i < 5 && container; i++) {
      priceInfo = parsePrice(container.textContent || '')
      if (priceInfo) break
      container = container.parentElement
    }
    if (!priceInfo || !container) return

    const docTitle = document.title.split(/[|\-–—]/)[0].trim()
    const title = [findTitleNear(container), docTitle].find(isValidTitle)
    // Skip this candidate if we couldn't resolve a real product title
    // (e.g. the page title is the editor's "Internal Preview").
    if (!title) return

    const key = `${title}|${priceInfo.num}`
    if (seen.has(key)) return
    seen.add(key)

    products.push({
      title,
      editionLabel: 'Edición',
      priceNumber: priceInfo.num,
      priceText: priceInfo.text,
      thumbnailUrl: findNearbyImage(container),
    })
  })

  return products
}

/**
 * Scans the current page and returns a catalog of purchasable products.
 * Falls back to a default weighted catalog when nothing is detected.
 */
export function scanPageProducts(): ScannedProduct[] {
  const structured = fromJsonLd()
  if (structured.length) return structured

  const heuristic = fromDomHeuristics()
  if (heuristic.length) return heuristic

  return []
}

/**
 * Returns a product to feature in a notification. Uses the scanned catalog
 * if available, otherwise the weighted fallback list.
 */
export function pickFeaturedProduct(catalog: ScannedProduct[]): ScannedProduct {
  if (catalog.length) {
    return catalog[Math.floor(Math.random() * catalog.length)]
  }
  return pickRandomFallback()
}
